/**
 * Public Commercial Property Registration — /register-property.
 * Iter-87. Dedicated route for commercial prospects; the resident tenant flow
 * at /register is intentionally kept untouched.
 *
 * Flow:
 *   1. Multifamily + 12 commercial types → dynamic Services checklist + type-specific fields
 *   2. Shared fields: Name, Full Address (Country/State/City/Zip),
 *      Primary Contact, Email, Phone, Preferred Start Date, Password
 *   3. Type-specific dynamic fields (unit count, sqft, floors, docks, etc.)
 *   4. Submit → POST /api/commercial/register → confirmation screen
 *
 * Demo mode: /register-property?demo=1 triggers is_demo=true and shows a
 * yellow "DEMO ENVIRONMENT" watermark so sales walkthroughs never mix with prod.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RegistrationAccessFields, registrationAmenityRows, registrationGateRows } from '../components/RegistrationAccessFields';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DEMO_AMENITIES } from '../lib/demoAmenities';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowRight, ArrowLeft, CheckCircle2, KeyRound, Eye, EyeOff, RefreshCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PropertyTypePicker } from '../components/PropertyTypePicker';
import { isSupportedPropertyType } from '../lib/propertyBusinessTypes';
import '../styles/property-intake.css';
import '../styles/public-page-titles.css';

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICE_LABELS = {
  valet_trash:          'Valet Trash',
  recycling:            'Recycling',
  bulk_removal:         'Bulk Removal',
  window_cleaning:      'Window Cleaning',
  pressure_washing:     'Pressure Washing',
  dumpster_management:  'Dumpster Management',
  grounds_cleanup:      'Grounds Cleanup',
  janitorial:           'Janitorial Services',
  trash_room_treatment: 'Trash Room & Dumpster Treatment',
};

// Iter-97 · Frequency options for the 4 new expansion services. Rendered
// as a lightweight dropdown beside each service pill when the service is
// checked so the property record captures the desired cadence at intake.
const EXPANSION_FREQ_OPTIONS = {
  janitorial:           ['Daily standard clean', 'Weekly deep clean'],
  trash_room_treatment: ['Monthly', 'Every 3 months'],
  pressure_washing:     ['Monthly', 'Every 3 months'],
  bulk_removal:         ['Weekly', 'Bi-weekly', 'Monthly'],
};

// Iter-87 · Type-specific dynamic fields per property type. Each entry defines
// the label, input type, and optional select options. Renders below the base
// address block once the type is selected.
const TYPE_SPECIFIC_FIELDS = {
  residential: [
    { key: 'building_count',        label: 'Number of buildings',   type: 'number' },
    { key: 'has_valet_currently',   label: 'Have valet trash today?', type: 'select', options: ['Yes', 'No'] },
    { key: 'lease_cycle',           label: 'Lease cycle',           type: 'select', options: ['12 months', '24 months', '36 months'] },
  ],
  office: [
    { key: 'tenant_count',          label: 'Number of tenants',     type: 'number' },
    { key: 'service_frequency',     label: 'Service frequency',     type: 'select', options: ['Daily', 'Weekly', 'Bi-weekly', 'Monthly'] },
  ],
  retail: [
    { key: 'lot_count',             label: 'Number of lots',        type: 'number' },
    { key: 'outdoor_bins',          label: 'Outdoor waste bins',    type: 'number' },
    { key: 'dumpster_count',        label: 'Dumpster count',        type: 'number' },
    { key: 'overnight_service',     label: 'Overnight service?',    type: 'select', options: ['Yes', 'No'] },
  ],
  gas_station: [
    { key: 'lot_count',             label: 'Number of lots',        type: 'number' },
    { key: 'outdoor_bins',          label: 'Outdoor waste bins',    type: 'number' },
    { key: 'dumpster_count',        label: 'Dumpster count',        type: 'number' },
    { key: 'overnight_service',     label: '24/7 station?',         type: 'select', options: ['Yes', 'No'] },
  ],
  school: [
    { key: 'student_capacity',      label: 'Student capacity',      type: 'number' },
    { key: 'bio_waste',             label: 'Bio-waste handling?',   type: 'select', options: ['Yes', 'No'] },
    { key: 'compliance_certs',      label: 'Compliance certs required', type: 'text' },
  ],
  medical: [
    { key: 'patient_capacity',      label: 'Patient / bed capacity', type: 'number' },
    { key: 'bio_waste',             label: 'Bio-waste handling?',   type: 'select', options: ['Yes', 'No'] },
    { key: 'compliance_certs',      label: 'Compliance certs required (HIPAA, OSHA, etc.)', type: 'text' },
  ],
  warehouse: [
    { key: 'loading_docks',         label: 'Loading dock count',    type: 'number' },
    { key: 'hazardous_waste',       label: 'Hazardous waste on site?', type: 'select', options: ['Yes', 'No'] },
    { key: 'service_window',        label: 'Preferred service window', type: 'select', options: ['Morning', 'Afternoon', 'Evening', 'Overnight'] },
  ],
  restaurant: [
    { key: 'seat_count',            label: 'Seat count',            type: 'number' },
    { key: 'service_hours',         label: 'Service hours',         type: 'select', options: ['Breakfast + Lunch', 'Lunch + Dinner', 'Dinner Only', 'All Day', 'Late Night'] },
  ],
  mall: [
    { key: 'anchor_tenants',        label: 'Anchor tenant count',   type: 'number' },
    { key: 'food_court_stalls',     label: 'Food court stall count', type: 'number' },
    { key: 'operating_hours',       label: 'Operating hours',       type: 'text' },
  ],
  // Iter-100 · Item 8 — Church / House of Worship registration fields.
  church: [
    { key: 'congregation_size',     label: 'Average congregation size', type: 'number' },
    { key: 'worship_service_count', label: 'Weekly worship services', type: 'number' },
    { key: 'has_daycare_school',    label: 'Onsite daycare / school?', type: 'select', options: ['Yes', 'No'] },
    { key: 'event_frequency',       label: 'Events per month',      type: 'select', options: ['0-2', '3-6', '7-12', '13+'] },
  ],
  other: [
    { key: 'facility_size_desc',    label: 'Facility size / description', type: 'text' },
    { key: 'service_frequency',     label: 'Service frequency',     type: 'select', options: ['Daily', 'Weekly', 'Bi-weekly', 'Monthly', 'On-call'] },
  ],
};

// Iter-95 · Realistic demo placeholder data per property type. When
// isDemo=true the entire form pre-populates on type select so the
// prospect can experience the flow end-to-end without typing anything.
const DEMO_PREFILLS = {
  residential: {
    demo_tier: null,  // NECS Valet packages are preserved outside this rebuild.
    property_name: 'Uptown Ridge Apartments',
    unit_count: '148', number_of_floors: '4',
    typeSpecific: { building_count: '3', has_valet_currently: 'Yes', lease_cycle: '12 months' },
    gate_code: '4821#', amenities: {
      'Pool': '8102', 'Fitness Center': '2004', 'Study Room': 'N/A', 'Clubhouse': '1150',
      'Mail Room': '3011', 'Parking Garage': '7702', 'Dog Park': 'N/A', 'Pet Spa': 'N/A',
    },
    services: ['valet_trash', 'recycling', 'bulk_removal'],
  },
  office: {
    demo_tier: 'standard',
    property_name: 'Crescent Tower Corporate Center',
    square_footage: '82000', number_of_floors: '11',
    typeSpecific: { tenant_count: '38', service_frequency: 'Weekly' },
    gate_code: 'CT-2024',
    amenities: { 'Parking Garage': 'P-1101', 'Fitness Center': 'F-901', 'Conference Rooms': 'CR-88', 'Cafeteria': 'N/A', 'Lobby': 'N/A' },
    services: ['window_cleaning', 'pressure_washing', 'dumpster_management'],
  },
  retail: {
    demo_tier: 'standard',
    property_name: 'Highland Grove Retail Plaza',
    square_footage: '45000',
    typeSpecific: { lot_count: '2', outdoor_bins: '18', dumpster_count: '6', overnight_service: 'Yes' },
    gate_code: 'N/A',
    amenities: { 'Parking Lot': 'N/A', 'Restrooms': 'RR-401', 'Loading Dock': 'LD-11', 'Storage Room': 'S-77' },
    services: ['window_cleaning', 'pressure_washing', 'grounds_cleanup'],
  },
  gas_station: {
    demo_tier: 'standard',
    property_name: 'Route 66 Fuel & Convenience #14',
    square_footage: '6500',
    typeSpecific: { lot_count: '1', outdoor_bins: '4', dumpster_count: '1', overnight_service: 'Yes' },
    gate_code: 'N/A',
    amenities: { 'Parking Lot': 'N/A', 'Restrooms': 'GK-4419', 'Storage Room': 'S-01', 'Loading Dock': 'N/A' },
    services: ['pressure_washing', 'dumpster_management', 'grounds_cleanup'],
  },
  school: {
    demo_tier: 'standard',
    property_name: 'Northbrook Elementary Campus',
    square_footage: '68000', number_of_floors: '2',
    typeSpecific: { student_capacity: '620', bio_waste: 'No', compliance_certs: 'OSHA compliant' },
    gate_code: 'STAFF-9910',
    amenities: { 'Gymnasium': 'GYM-01', 'Cafeteria': 'CAF-04', 'Library': 'LIB-11', 'Playground': 'N/A', 'Administrative Office': 'ADM-100' },
    services: ['window_cleaning', 'dumpster_management', 'grounds_cleanup'],
  },
  medical: {
    demo_tier: 'premium',
    property_name: 'Cedar Point Family Clinic',
    square_footage: '18000', number_of_floors: '3',
    typeSpecific: { patient_capacity: '80', bio_waste: 'Yes', compliance_certs: 'HIPAA, OSHA' },
    gate_code: 'MED-7220',
    amenities: { 'Waiting Room': 'N/A', 'Laboratory': 'LAB-3', 'Break Room': 'BR-2', 'Administrative Office': 'ADM-12' },
    services: ['window_cleaning', 'pressure_washing', 'dumpster_management'],
  },
  warehouse: {
    demo_tier: 'standard',
    property_name: 'Interstate Logistics Hub 12',
    square_footage: '130000',
    typeSpecific: { loading_docks: '14', hazardous_waste: 'No', service_window: 'Overnight' },
    gate_code: 'GATE-4210',
    amenities: { 'Loading Dock': 'LD-14', 'Break Room': 'BR-9', 'Storage Areas': 'STG-77', 'Security Booth': 'SEC-01', 'Parking Lot': 'N/A' },
    services: ['pressure_washing', 'bulk_removal', 'dumpster_management'],
  },
  restaurant: {
    demo_tier: 'standard',
    property_name: 'Lantern Lane Bistro',
    square_footage: '5200',
    typeSpecific: { seat_count: '84', service_hours: 'Lunch + Dinner' },
    gate_code: 'DINE-1120',
    amenities: {
      'Kitchen': 'KIT-01', 'Dining Room': 'N/A', 'Bar Area': 'BAR-12',
      'Restrooms': 'RR-4', 'Grease Trap Area': 'GT-2', 'Patio / Outdoor Seating': 'N/A',
    },
    services: ['dumpster_management', 'janitorial', 'pressure_washing', 'grounds_cleanup'],
  },
  mall: {
    demo_tier: 'premium',
    property_name: 'Harbor Point Shopping Center',
    square_footage: '480000',
    typeSpecific: { anchor_tenants: '4', food_court_stalls: '9', operating_hours: '10am-9pm daily' },
    gate_code: 'MALL-7708',
    amenities: {
      'Common Areas': 'CA-01', 'Food Court': 'FC-04', 'Parking Garage': 'PG-11',
      'Restrooms': 'RR-6', 'Loading Dock': 'LD-3', 'Storage Room': 'STG-88',
      'Security Booth': 'SEC-01', 'Elevators': 'EL-2', 'Escalators': 'ES-3',
    },
    services: ['dumpster_management', 'janitorial', 'pressure_washing', 'window_cleaning', 'bulk_removal', 'grounds_cleanup'],
  },
  // Iter-100 · Item 8 — Church / House of Worship live demo prefill.
  church: {
    demo_tier: 'standard',
    property_name: 'Grace & Glory Community Church',
    square_footage: '28000', number_of_floors: '2',
    typeSpecific: { congregation_size: '650', worship_service_count: '4', has_daycare_school: 'Yes', event_frequency: '7-12' },
    gate_code: 'CHURCH-9910',
    amenities: {
      'Sanctuary': 'SANC-01', 'Fellowship Hall': 'FH-04', 'Nursery': 'NUR-11',
      'Classrooms': 'CR-77', 'Kitchen': 'KIT-12', 'Restrooms': 'RR-6',
      'Parking Lot': 'N/A', 'Sound Booth': 'AV-01',
    },
    services: ['janitorial', 'grounds_cleanup', 'pressure_washing', 'dumpster_management'],
  },
  other: {
    demo_tier: null,  // Custom always needs sales review.
    property_name: 'Downtown Event Venue',
    square_footage: '22000',
    typeSpecific: { facility_size_desc: '3-story mixed-use venue', service_frequency: 'Weekly' },
    gate_code: 'EV-2100',
    amenities: { 'Parking Lot': 'N/A', 'Restrooms': 'RR-2', 'Storage Room': 'ST-1' },
    services: ['window_cleaning', 'grounds_cleanup', 'dumpster_management'],
  },
};

const DEMO_BASE_CONTACT = {
  country: 'USA', state: 'TX', city: 'Dallas', zip_code: '75201',
  street_address: '1220 Main Street, Suite 500',
  primary_contact: 'Alex Rodriguez',
  email: '', phone: '(214) 555-0142',
  preferred_start_date: (() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  })(),
  notes: 'Interested in a walk-through this week.',
};

export default function RegisterPropertyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isDemo = params.get('demo') === '1';
  const [step, setStep] = useState(1);           // 1 = type · 2 = form · 3 = confirm
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({
    property_name: '', country: 'USA', state: '', city: '', zip_code: '',
    street_address: '', primary_contact: '', email: '', phone: '',
    preferred_start_date: '', unit_count: '', square_footage: '',
    number_of_floors: '', notes: '', property_type_other: '',
    password: '', confirm_password: '',
    // Iter-190 · Position/sub-role chosen at signup. Flows to
    // IdentityBadge in the dashboard top-logo menu once activated.
    pm_sub_role: '',
    // Iter-95 · Universal Gate Code + consents.
    gate_code: '',
    sms_opt_in: false,
    notifications_consent: false,
    // Iter-104 · Item 4 — Property Plan Upload (all 9 registration forms).
    plan_total_sqft: '', plan_floor_count: '', plan_rooms_units: '',
    plan_lot_size: '', plan_layout_notes: '',
    property_plan_file_name: '',
  });
  const [propertyPlanFile, setPropertyPlanFile] = useState(null);
  // Iter-108 · Password visibility toggles + live strength/match logic.
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const passwordStrength = useMemo(() => scorePassword(form.password || ''), [form.password]);
  const passwordsMatch = !!form.password && form.password === form.confirm_password;
  // Iter-108.2 · Copy-Password Guard state — flashes a warning when a
  // registrant pastes something that looks like an email or company
  // name into either password field.
  const [pastedNotice, setPastedNotice] = useState(false);
  const guardPaste = (e, _field) => {
    try {
      const txt = (e.clipboardData?.getData('text') || '').trim();
      if (!txt) return;
      const looksSuspicious =
        /@/.test(txt) ||                                        // email-ish
        /\s(inc|llc|corp|company|group|properties)\b/i.test(txt) ||
        /\bnoble empire\b/i.test(txt);
      if (looksSuspicious) {
        setPastedNotice(true);
        setTimeout(() => setPastedNotice(false), 6000);
      }
    } catch { /* silent */ }
  };
  const [services, setServices] = useState([]);
  const [typeSpecific, setTypeSpecific] = useState({});
  // Iter-96 · Selected NECS tier ('basic'|'standard'|'premium'|null=recommend).
  const [tier, setTier] = useState(null);
  // Master Addendum · Multifamily-only package selection. When a
  // property manager registers a residential (Multifamily) community
  // they pick one of the 6 NECS Collections (Essential → Presidential).
  // Non-multifamily types keep using the `tier` selector above.
  const [multifamilyPackage, setMultifamilyPackage] = useState('');
  // Iter-97 · Frequency selections for the 4 expansion services. Only the
  // ones the registrant checked in Services Needed are surfaced. Keyed by
  // service key with a free-text value from EXPANSION_FREQ_OPTIONS.
  const [serviceFrequencies, setServiceFrequencies] = useState({});
  // amenities: { [name]: { checked, entry_code } }
  const [amenities, setAmenities] = useState({});
  const [geoBadge, setGeoBadge] = useState({ visible: false, fading: false });
  // Iter-109 · Save Draft & Finish Later — drafts keyed by email, 30-day TTL.
  const [draftMsg, setDraftMsg] = useState(null);   // { ok, text }
  const [draftBusy, setDraftBusy] = useState(false);
  const [resumeEmail, setResumeEmail] = useState('');
  const [resumeBusy, setResumeBusy] = useState(false);
  // Iter-110 · Item 4 — auto-save + returning-registrant resume prompt.
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const [resumePrompt, setResumePrompt] = useState(null); // { email, saved_at, data }
  const draftCheckedEmails = useRef(new Set());

  const applyDraft = (d) => {
    const dr = d.draft || {};
    if (dr.form) setForm((f) => ({ ...f, ...dr.form, password: '', confirm_password: '' }));
    setServices(dr.services || []);
    setTypeSpecific(dr.typeSpecific || {});
    setTier(dr.tier ?? null);
    setServiceFrequencies(dr.serviceFrequencies || {});
    if (dr.amenities && Object.keys(dr.amenities).length) setAmenities(dr.amenities);
    const t = types.find((x) => x.key === d.property_type);
    if (t) {
      setSelectedType(t);
      setStep(2);
    }
  };

  const saveDraft = async () => {
    setDraftMsg(null);
    const email = (form.email || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setDraftMsg({ ok: false, text: 'Enter your email in the form first — your draft is keyed to it.' });
      return;
    }
    setDraftBusy(true);
    try {
      const { password, confirm_password, ...safeForm } = form;
      const res = await fetch(`${API}/api/registration-drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          property_type: selectedType?.key || null,
          draft: { form: safeForm, services, typeSpecific, tier, serviceFrequencies, amenities },
        }),
      });
      if (res.ok) {
        setDraftMsg({ ok: true, text: `Draft saved for ${email}. Come back anytime within 30 days and resume from the first step. (Passwords and file attachments are not stored — re-enter them when you return.)` });
      } else {
        setDraftMsg({ ok: false, text: 'Could not save your draft — please try again.' });
      }
    } catch {
      setDraftMsg({ ok: false, text: 'Network error while saving your draft.' });
    } finally {
      setDraftBusy(false);
    }
  };

  const resumeDraft = async () => {
    const email = resumeEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setDraftMsg({ ok: false, text: 'Enter the email you used when saving the draft.' });
      return;
    }
    setResumeBusy(true);
    setDraftMsg(null);
    try {
      const res = await fetch(`${API}/api/registration-drafts?email=${encodeURIComponent(email)}`);
      const d = res.ok ? await res.json() : null;
      if (!d || !d.found) {
        setDraftMsg({ ok: false, text: 'No saved draft found for that email (drafts expire after 30 days).' });
        return;
      }
      const dr = d.draft || {};
      void dr;
      applyDraft(d);
      draftCheckedEmails.current.add(email);
      setDraftMsg({ ok: true, text: 'Draft restored — pick up right where you left off. Re-enter your password before submitting.' });
    } catch {
      setDraftMsg({ ok: false, text: 'Network error while loading your draft.' });
    } finally {
      setResumeBusy(false);
    }
  };

  // Iter-110 · Item 4 — Auto-save draft (debounced 2.5s) once a valid email
  // is present on a real (non-demo) registration. The very first time an
  // email is seen we check for an existing draft and offer Resume instead
  // of silently overwriting the returning registrant's saved work.
  useEffect(() => {
    if (isDemo || step !== 2 || result || resumePrompt) return undefined;
    const email = (form.email || '').trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return undefined;
    const timer = setTimeout(async () => {
      try {
        if (!draftCheckedEmails.current.has(email)) {
          draftCheckedEmails.current.add(email);
          const res = await fetch(`${API}/api/registration-drafts?email=${encodeURIComponent(email)}`);
          const d = res.ok ? await res.json() : null;
          if (d && d.found) {
            setResumePrompt({ email, saved_at: d.saved_at, data: d });
            return;
          }
        }
        const { password, confirm_password, ...safeForm } = form;
        const res = await fetch(`${API}/api/registration-drafts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            property_type: selectedType?.key || null,
            draft: { form: safeForm, services, typeSpecific, tier, serviceFrequencies, amenities },
          }),
        });
        if (res.ok) {
          setAutoSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        }
      } catch { /* silent */ }
    }, 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, services, typeSpecific, tier, serviceFrequencies, amenities, step, isDemo, result, resumePrompt, selectedType]);

  const loadTypes = useCallback(async () => {
    setTypesLoading(true);
    setTypesError('');
    try {
      const response = await fetch(`${API}/api/commercial/property-types`);
      if (!response.ok) throw new Error('Catalog unavailable');
      const data = await response.json();
      const supported = (data.types || []).filter(type => isSupportedPropertyType(type.key));
      if (supported.length !== 13) throw new Error('Incomplete catalog');
      setTypes(supported);
    } catch {
      setTypesError('Property types could not be loaded. Please try again.');
    } finally {
      setTypesLoading(false);
    }
  }, []);
  useEffect(() => { loadTypes(); }, [loadTypes]);

  // Iter-103 · Item 8 — Sandbox hydration from Live Demo submission.
  // /register-property?demo=1&session=<token> — fetches the persisted
  // Live Demo form values and pre-fills the sandbox so it looks like a
  // real account from first render. Auto-advances to step 2 (form) so
  // the visitor lands directly on the pre-filled sandbox view.
  useEffect(() => {
    const session = params.get('session');
    const typeHint = params.get('type');
    if (!session) return;
    fetch(`${API}/api/live-demo/session/${session}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        const s = d.submitted_fields || {};
        const NAME = s.property_name || s.business_name || s.practice_name ||
                     s.restaurant_name || s.facility_name || s.institution_name ||
                     s.center_name || s.mall_name || s.church_name || d.property_name;
        setForm((f) => ({
          ...f,
          property_name:  NAME || f.property_name,
          email:          s.contact_email || d.contact_email || f.email,
          phone:          s.contact_phone || d.contact_phone || f.phone,
          street_address: s.address || d.address || f.street_address,
          notes:          s.notes || d.notes || f.notes,
          gate_code:      s.gate_code || f.gate_code,
          square_footage: s.plan_total_sqft || s.square_footage || f.square_footage,
          number_of_floors: s.plan_floor_count || f.number_of_floors,
          unit_count:     s.unit_count || s.plan_rooms_units || s.building_count || f.unit_count,
        }));
      })
      .catch(() => {});
  }, [params]);

  // Iter-103 · Item 8 — Auto-select the type + advance to step 2 once
  // both the type catalog AND the session hint are ready. Runs whenever
  // `types` change so we don't miss the race between the two fetches.
  useEffect(() => {
    const typeHint = params.get('type');
    const hasSession = !!params.get('session');
    if (!hasSession || !typeHint || !types.length || selectedType) return;
    const found = types.find((t) => t.key === typeHint);
    if (found) {
      setSelectedType(found);
      setStep(2);
    }
  }, [types, params, selectedType]);

  // Iter-88 · Auto-detect Country/State on mount via browser geolocation +
  // BigDataCloud (no key, no CORS). Runs once; skipped if unsupported.
  useEffect(() => {
    if (!('geolocation' in navigator)) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (cancelled) return;
        try {
          const { latitude, longitude } = pos.coords;
          const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`;
          const res = await fetch(url);
          if (!res.ok) return;
          const data = await res.json();
          if (cancelled) return;
          // Convert principalSubdivisionCode "US-TX" -> "TX" for state code.
          const rawState = data.principalSubdivisionCode || '';
          const stateCode = rawState.includes('-') ? rawState.split('-')[1] : (data.principalSubdivision || '');
          const countryCode = data.countryCode || '';
          const detectedCountry = countryCode === 'US' ? 'USA' : (data.countryName || '');
          setForm((f) => {
            // Only overwrite when the user hasn't touched the field.
            // Country default is 'USA' at mount, so treat that as untouched.
            const nextCountry = (!f.country || f.country === 'USA') && detectedCountry ? detectedCountry : f.country;
            const nextState = !f.state && stateCode ? stateCode : f.state;
            const changed = nextCountry !== f.country || nextState !== f.state;
            if (changed) {
              setGeoBadge({ visible: true, fading: false });
              // Kick off the fade after 3s; unmount at 4s.
              setTimeout(() => setGeoBadge((g) => (g.visible ? { visible: true, fading: true } : g)), 3000);
              setTimeout(() => setGeoBadge({ visible: false, fading: false }), 4000);
            }
            return { ...f, country: nextCountry, state: nextState };
          });
        } catch {
          /* silent — form remains fully manual */
        }
      },
      () => { /* permission denied / timeout — silent */ },
      { timeout: 8000, maximumAge: 600000 }
    );
    return () => { cancelled = true; };
  }, []);

  const chooseType = (t) => {
    setSelectedType(t);
    setTier(null);   // Reset tier on every type change.
    // Seed amenity checklist for this type — unchecked, empty entry codes.
    const seed = {};
    (t.amenities || []).forEach((name) => {
      seed[name] = { checked: false, entry_code: '' };
    });

    if (isDemo) {
      const pf = DEMO_PREFILLS[t.key] || {};
      const emailBase = `demo_${t.key}_${Math.random().toString(36).slice(2, 8)}@necsdemo.com`;
      // Pre-fill contact + property fields.
      setForm((f) => ({
        ...f,
        ...DEMO_BASE_CONTACT,
        email: emailBase,
        property_name: pf.property_name || `Demo ${t.label}`,
        unit_count: pf.unit_count || f.unit_count,
        square_footage: pf.square_footage || f.square_footage,
        number_of_floors: pf.number_of_floors || f.number_of_floors,
        gate_code: pf.gate_code || 'N/A',
        gate_locations: undefined,
        password: 'demo1234',
        confirm_password: 'demo1234',
        sms_opt_in: true,
        notifications_consent: true,
        property_type_other: t.key === 'other' ? 'Mixed-use venue (demo)' : '',
      }));
      setServices(pf.services || []);
      setTypeSpecific(pf.typeSpecific || {});
      // Iter-96 · Auto-select the demo tier if applicable.
      setTier(pf.demo_tier || null);
      // Demo presets remain editable, alongside the full vertical catalog.
      (DEMO_AMENITIES[t.key] || []).forEach((a) => {
        seed[a.name] = { checked: true, entry_code: 'N/A', quantity: a.qty };
      });
    } else {
      // Non-demo — reset services + type-specific but keep the base
      // fields the user has already filled in.
      setServices([]);
      setTypeSpecific({});
    }

    setAmenities(seed);
    setStep(2);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateTS = (k, v) => setTypeSpecific((s) => ({ ...s, [k]: v }));

  const toggleService = (key) => {
    setServices((cur) => (cur.includes(key) ? cur.filter((s) => s !== key) : [...cur, key]));
  };

  const dynamicFields = useMemo(() => {
    if (!selectedType) return [];
    return TYPE_SPECIFIC_FIELDS[selectedType.key] || [];
  }, [selectedType]);

  const showsUnitCount = selectedType && selectedType.key === 'residential';
  const showsSquareFootage = selectedType && ['office', 'retail', 'warehouse', 'medical', 'school', 'church', 'other'].includes(selectedType.key);
  const showsFloors = selectedType && ['office', 'medical'].includes(selectedType.key);
  const showsTypeOther = selectedType && selectedType.key === 'other';

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    // Iter-95 · Front-end validation before hitting the API.
    if (form.password && form.password !== form.confirm_password) {
      setErr('Passwords do not match.');
      return;
    }
    if (!form.sms_opt_in) {
      setErr('Please confirm Twilio SMS opt-in to continue.');
      return;
    }
    if (!form.notifications_consent) {
      setErr('Please confirm app notification consent to continue.');
      return;
    }
    setSubmitting(true);
    try {
      // Flatten amenities to a POST-ready list. Entry codes default to N/A.
      const amenitiesPayload = Object.entries(amenities || {})
        .flatMap(([name, value]) => registrationAmenityRows(value).map((v) => ({
          name,
          location_name: v.location_name,
          entry_code: (v.entry_code || '').trim() || 'N/A',
          ...(v.quantity != null ? { quantity: v.quantity } : {}),
        })));
      const payload = {
        ...form,
        property_type: selectedType.key,
        services_needed: services,
        type_specific: typeSpecific,
        is_demo: isDemo,
        password: form.password || null,
        gate_code: (form.gate_code || '').trim() || 'N/A',
        gate_locations: registrationGateRows(form).map(({ editing, ...row }) => ({ ...row, entry_code: (row.entry_code || '').trim() || 'N/A' })),
        amenities: amenitiesPayload,
        sms_opt_in: !!form.sms_opt_in,
        notifications_consent: !!form.notifications_consent,
        // Iter-96 · Named service line + tier (null → sales_review_flag).
        service_line_name: selectedType.necs_name,
        tier: tier || null,
        multifamily_package: multifamilyPackage || null,
        service_frequencies: serviceFrequencies || {},
        unit_count: form.unit_count ? Number(form.unit_count) : null,
        square_footage: form.square_footage ? Number(form.square_footage) : null,
        number_of_floors: form.number_of_floors ? Number(form.number_of_floors) : null,
      };
      // Strip confirm_password before shipping.
      delete payload.confirm_password;
      const res = await fetch(`${API}/api/commercial/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const registrationResult = await res.json();
        setResult(registrationResult);

        // Iter-109 · Registration complete — clear any saved draft.
        if (form.email) {
          fetch(`${API}/api/registration-drafts?email=${encodeURIComponent(form.email.trim().toLowerCase())}`, { method: 'DELETE' }).catch(() => {});
        }

        // Iter-105 · Item 3 — Upload the property plan file bytes to
        // Emergent Object Storage tied to the new property_id. Silent
        // on failure — never block the registration success flow.
        const propertyId = registrationResult?.property?.property_id
                        || registrationResult?.property_id;
        if (propertyId && propertyPlanFile) {
          try {
            const fd = new FormData();
            fd.append('property_id', propertyId);
            fd.append('file', propertyPlanFile);
            await fetch(`${API}/api/property-plans/upload`, {
              method: 'POST',
              body: fd,
              credentials: 'include',
            });
          } catch { /* silent — non-blocking */ }
        }

        setStep(3);
      } else {
        const t = await res.json().catch(() => ({}));
        setErr(t.detail || 'Registration failed — please try again or email support@nobleempirecs.com');
      }
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Iter-96c · Step-aware backdrop. Step 1 → NECS crew hero. Step 2/3
  // → vans + building hero so the commercial context reads at a glance.
  return (
    <div
      className="property-intake-page mobile-form-surface"
      data-testid="register-property-page"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        {/* Iter-96b · Back button — returns to /login (or history if available). */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              // Prefer real browser history so demos and direct links both work;
              // fall back to /login when there is no previous entry.
              if (window.history.length > 1) navigate(-1);
              else navigate('/login');
            }}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-teal-300"
            data-testid="register-back-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>
        </div>
        <div className="mt-8 text-center">
          <p
            className="text-[10px] uppercase tracking-[0.4em] mb-2 text-teal-300"
            data-testid="register-subheading-portal"
          >
            · NECS Property Registration ·
          </p>
          {isDemo && (
            <div
              className="mb-4 py-2 px-4 rounded-lg text-center font-black uppercase tracking-widest"
              style={{ backgroundColor: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '2px dashed #fbbf24' }}
              data-testid="register-demo-watermark"
            >
              ⚠ DEMO ENVIRONMENT · Submissions are watermarked and isolated from production
            </div>
          )}
          <h1 className="necs-web-page-title text-white mb-3" data-testid="register-property-heading">
            REGISTER YOUR <span className="necs-web-title-accent">PROPERTY</span>
          </h1>
          <p className="text-sm text-zinc-300 max-w-2xl mx-auto" data-testid="register-property-description">Commercial and multifamily — get onboarded in under a business day.</p>
        </div>

        {/* Stepper */}
        {step > 1 && <div className="flex flex-wrap items-center justify-center gap-2 mt-8 text-[11px] uppercase tracking-widest">
          {[1, 2, 3].map((n) => (
            <span
              key={n}
              className="px-3 py-1 rounded-full font-bold"
              style={{
                backgroundColor: step >= n ? '#C9A962' : 'rgba(201,169,98,0.15)',
                color: step >= n ? '#1a1a1a' : '#C9A962',
              }}
              data-testid={`register-step-${n}`}
            >
              {n === 1 ? 'Type' : n === 2 ? 'Details' : 'Confirmed'}
            </span>
          ))}
        </div>}

        {step === 1 && (
          <div className="mt-10" data-testid="register-step-type-card">
            {typesLoading ? <p className="text-sm text-center text-zinc-200" role="status" data-testid="register-types-loading">Loading property types…</p>
              : typesError ? <div className="text-center" role="alert" data-testid="register-types-error">
                <p className="text-sm text-red-200" data-testid="register-types-error-message">{typesError}</p>
                <Button type="button" onClick={loadTypes} className="mt-3 bg-[#C9A962] text-black" data-testid="register-types-retry">Try again</Button>
              </div>
              : <PropertyTypePicker types={types} onSelect={chooseType} testIdPrefix="register" showDetails />}

            {/* Iter-109 · Resume a saved draft — same email, no lost work. */}
            {!isDemo && (
            <div className="mt-6 pt-5" style={{ borderTop: '1px solid rgba(201,169,98,0.25)' }} data-testid="register-resume-draft-block">
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#C9A962' }}>
                Started before? Resume your saved draft
              </p>
              <div className="flex flex-col sm:flex-row gap-2 max-w-md">
                <Input
                  type="email"
                  value={resumeEmail}
                  onChange={(e) => setResumeEmail(e.target.value)}
                  placeholder="Email you used on the draft"
                  className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                  data-testid="register-resume-email-input"
                />
                <Button
                  type="button"
                  onClick={resumeDraft}
                  disabled={resumeBusy || !resumeEmail.trim()}
                  className="font-bold text-xs whitespace-nowrap"
                  style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
                  data-testid="register-resume-draft-btn"
                >
                  {resumeBusy ? 'Loading…' : 'Resume Draft'}
                </Button>
              </div>
              {draftMsg && (
                <p className="text-xs mt-2" style={{ color: draftMsg.ok ? '#34d399' : '#f87171' }} data-testid="register-draft-msg">
                  {draftMsg.text}
                </p>
              )}
            </div>
            )}
          </div>
        )}

        {step === 2 && selectedType && (
          <div className="mt-10 max-w-3xl mx-auto">
            {/* Iter-108.2 · Change business type pill — white bg,
                gold border/text/icon, matches the demo form layout. */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full transition"
                style={{ backgroundColor: '#ffffff', color: '#C9A962', border: '1px solid #C9A962' }}
                data-testid="register-back-to-type"
              >
                <ArrowLeft className="h-3 w-3" /> Change business type
              </button>
              <span
                className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold"
                style={{ backgroundColor: 'rgba(201,169,98,0.15)', color: '#C9A962' }}
                data-testid="register-selected-type"
              >
                {selectedType.necs_name}
              </span>
            </div>

          <Card
            className="p-6 border-zinc-700 backdrop-blur-md shadow-2xl"
            style={{ backgroundColor: 'rgba(20,20,20,0.88)' }}
            data-testid="register-step-form-card"
          >
            <div className="mb-4">
              <h2 className="text-lg font-bold text-white">2 · {selectedType.necs_name} · {selectedType.label} Details</h2>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <Row>
                <Field label="Property Name *" testid="register-property-name">
                  <Input required value={form.property_name} onChange={(e) => update('property_name', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" />
                </Field>
                {showsTypeOther && (
                  <Field label="Describe your property *" testid="register-property-type-other">
                    <Input required value={form.property_type_other} onChange={(e) => update('property_type_other', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" />
                  </Field>
                )}
              </Row>

              <Row>
                <Field label="Country *" badge={geoBadge.visible ? <GeoBadge fading={geoBadge.fading} /> : null}><Input required value={form.country} onChange={(e) => update('country', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-country" /></Field>
                <Field label="State *" badge={geoBadge.visible ? <GeoBadge fading={geoBadge.fading} /> : null}><Input required value={form.state} onChange={(e) => update('state', e.target.value)} placeholder="e.g. TX" className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-state" /></Field>
                <Field label="City *"><Input required value={form.city} onChange={(e) => update('city', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-city" /></Field>
                <Field label="ZIP *"><Input required value={form.zip_code} onChange={(e) => update('zip_code', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-zip" /></Field>
              </Row>

              <Field label="Street Address *">
                <Input required value={form.street_address} onChange={(e) => update('street_address', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-street" />
              </Field>

              <Row>
                <Field label="Primary Contact *"><Input required value={form.primary_contact} onChange={(e) => update('primary_contact', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-contact" /></Field>
                <Field label="Email *"><Input required type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-email" /></Field>
                <Field label="Phone *"><Input required value={form.phone} onChange={(e) => update('phone', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-phone" /></Field>
                <Field label="Preferred Start Date *"><Input required type="date" value={form.preferred_start_date} onChange={(e) => update('preferred_start_date', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-start-date" /></Field>
              </Row>

              {(showsUnitCount || showsSquareFootage || showsFloors) && (
                <Row>
                  {showsUnitCount && <Field label="Unit Count"><Input type="number" value={form.unit_count} onChange={(e) => update('unit_count', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-unit-count" /></Field>}
                  {showsSquareFootage && <Field label="Square Footage"><Input type="number" value={form.square_footage} onChange={(e) => update('square_footage', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-sqft" /></Field>}
                  {showsFloors && <Field label="Number of Floors"><Input type="number" value={form.number_of_floors} onChange={(e) => update('number_of_floors', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="register-floors" /></Field>}
                </Row>
              )}

              {/* Iter-87 · Dynamic type-specific fields */}
              {dynamicFields.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#C9A962] mb-2 font-bold">
                    {selectedType.label} · Specifics
                  </p>
                  <Row>
                    {dynamicFields.map((df) => (
                      <Field key={df.key} label={df.label} testid={`register-ts-${df.key}`}>
                        {df.type === 'select' ? (
                          <select
                            value={typeSpecific[df.key] || ''}
                            onChange={(e) => updateTS(df.key, e.target.value)}
                            className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-white text-sm"
                          >
                            <option value="">Select…</option>
                            {df.options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <Input
                            type={df.type}
                            value={typeSpecific[df.key] || ''}
                            onChange={(e) => updateTS(df.key, e.target.value)}
                            className="bg-zinc-800 border-zinc-700 text-white"
                          />
                        )}
                      </Field>
                    ))}
                  </Row>
                </div>
              )}

              {/* Iter-110 · Item 7 — Property Plan Upload renders ONLY on
                  real registrations. Removed from all demo forms. */}
              {!isDemo && (
              <div
                className="p-4 rounded-lg border"
                style={{ borderColor: 'rgba(201,169,98,0.4)', backgroundColor: 'rgba(201,169,98,0.08)' }}
                data-testid="register-property-plan-block"
              >
                <p className="text-xs uppercase tracking-widest text-[#C9A962] mb-1 font-bold">
                  Upload Property Plan
                </p>
                <p className="text-[11px] text-zinc-400 mb-3">
                  Upload your property plan (PDF, PNG, JPG, DWG, or DXF). Provide the
                  layout details below so we can scope service correctly.
                </p>
                <div className="mb-3">
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-300 mb-1">
                    Property plan file
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setPropertyPlanFile(f);
                      update('property_plan_file_name', f?.name || '');
                    }}
                    className="w-full text-xs text-zinc-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#C9A962] file:text-black file:font-bold"
                    data-testid="register-property-plan-file"
                  />
                  {form.property_plan_file_name && (
                    <p className="text-[10px] text-zinc-400 mt-1">Selected: {form.property_plan_file_name}</p>
                  )}
                </div>
                <Row>
                  <Field label="Total square footage" testid="register-plan-sqft">
                    <Input
                      type="number"
                      value={form.plan_total_sqft}
                      onChange={(e) => update('plan_total_sqft', e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </Field>
                  <Field label="Number of floors" testid="register-plan-floors">
                    <Input
                      type="number"
                      value={form.plan_floor_count}
                      onChange={(e) => update('plan_floor_count', e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </Field>
                  <Field label="Number of rooms / units" testid="register-plan-rooms">
                    <Input
                      type="number"
                      value={form.plan_rooms_units}
                      onChange={(e) => update('plan_rooms_units', e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </Field>
                  <Field label="Lot size" testid="register-plan-lot">
                    <Input
                      value={form.plan_lot_size}
                      onChange={(e) => update('plan_lot_size', e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </Field>
                </Row>
                <Field label="Notes about property layout">
                  <textarea
                    rows={3}
                    value={form.plan_layout_notes}
                    onChange={(e) => update('plan_layout_notes', e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-white text-sm"
                    data-testid="register-plan-notes"
                  />
                </Field>
              </div>
              )}

              {/* Master Addendum · Multifamily service package selector.
                  Only rendered for the 'residential' (Multifamily)
                  property type — commercial verticals continue to use
                  the tier selector below. */}
              {selectedType?.key === 'residential' && (
                <div className="mt-6" data-testid="register-multifamily-package-section">
                  <p className="text-xs uppercase tracking-widest text-[#C9A962] font-bold mb-2">
                    NECS Multifamily · Service Package
                  </p>
                  <p className="text-[11px] text-zinc-400 mb-2">
                    Choose the Collection you'd like your community to launch with. Your NECS account
                    manager will confirm final pricing and any customizations before the contract goes out.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {[
                      'Essential Collection',
                      'Professional Collection',
                      'Elite Collection',
                      'Signature Collection',
                      'Noble Empire Executive Collection',
                      'Noble Empire Presidential Collection',
                    ].map((pkg) => {
                      const active = multifamilyPackage === pkg;
                      return (
                        <button
                          key={pkg}
                          type="button"
                          onClick={() => setMultifamilyPackage(active ? '' : pkg)}
                          className="text-left p-3 rounded-lg border transition"
                          style={{
                            borderColor: active ? '#C9A962' : '#3f3f46',
                            backgroundColor: active ? 'rgba(201,169,98,0.12)' : '#0a0a0a',
                          }}
                          data-testid={`register-mf-package-${pkg.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <span
                            className="text-sm font-black uppercase tracking-wider block"
                            style={{
                              color: active ? '#C9A962' : '#e4e4e7',
                              fontFamily: 'Barlow Condensed, sans-serif',
                            }}
                          >
                            {active ? '✓ ' : ''}{pkg}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-zinc-500 italic mt-2">
                    Leave blank if you'd rather let a NECS account manager recommend the right Collection.
                  </p>
                </div>
              )}

              {/* Iter-96 · NECS tier selector. Custom skips this entirely. */}
              {selectedType.tiers && selectedType.tiers.length > 0 && !selectedType.custom_only && (
                <div data-testid="register-tier-selector">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-widest text-[#C9A962] font-bold">
                      {selectedType.necs_name} · Package Tier
                    </p>
                    <button
                      type="button"
                      onClick={() => { setTier(null); }}
                      className="text-[11px] text-zinc-400 hover:text-white transition"
                      data-testid="register-tier-recommend"
                    >
                      {tier === null ? '✓ ' : ''}Let my account manager recommend
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {selectedType.tiers.map((t) => {
                      const active = tier === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => {
                            setTier(t.key);
                            // Auto-check tier services as SUGGESTIONS
                            // (user may still toggle any of them).
                            setServices((prev) => {
                              const merged = new Set([...(prev || []), ...(t.services || [])]);
                              return Array.from(merged);
                            });
                          }}
                          className="text-left p-4 rounded-lg border transition"
                          style={{
                            borderColor: active ? '#C9A962' : '#3f3f46',
                            backgroundColor: active ? 'rgba(201,169,98,0.12)' : '#0a0a0a',
                          }}
                          data-testid={`register-tier-${t.key}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span
                              className="text-lg font-black uppercase tracking-wider"
                              style={{
                                color: active ? '#C9A962' : '#e4e4e7',
                                fontFamily: 'Barlow Condensed, sans-serif',
                              }}
                            >
                              {t.name}
                            </span>
                            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                              {t.frequency}
                            </span>
                          </div>
                          <ul className="text-[11px] text-zinc-300 space-y-1">
                            {t.included.map((line, i) => (
                              <li key={i} className="flex gap-1.5 items-start">
                                <span style={{ color: '#C9A962' }}>•</span>
                                <span>{line}</span>
                              </li>
                            ))}
                          </ul>
                          <p className="text-[10px] text-zinc-500 italic mt-3">
                            Final pricing confirmed by your NECS account manager.
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedType.custom_only && (
                <div className="p-4 rounded-lg border" style={{ borderColor: '#C9A962', backgroundColor: 'rgba(201,169,98,0.08)' }} data-testid="register-custom-note">
                  <p className="text-xs uppercase tracking-widest text-[#C9A962] font-bold mb-1">
                    NECS Custom
                  </p>
                  <p className="text-[12px] text-zinc-300">
                    Your NECS account manager will build your package after reviewing your services checklist below.
                    Final pricing confirmed by your NECS account manager.
                  </p>
                </div>
              )}

              {/* Services checklist filtered by property type */}
              <div>
                <p className="text-xs uppercase tracking-widest text-[#C9A962] mb-2 font-bold">Services Needed</p>
                <div className="flex flex-wrap gap-2" data-testid="register-services-checklist">
                  {selectedType.services.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleService(s)}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold transition"
                      style={{
                        backgroundColor: services.includes(s) ? '#C9A962' : 'rgba(201,169,98,0.1)',
                        color: services.includes(s) ? '#1a1a1a' : '#C9A962',
                        border: `1px solid ${services.includes(s) ? '#C9A962' : 'rgba(201,169,98,0.4)'}`,
                      }}
                      data-testid={`register-service-${s}`}
                    >
                      {SERVICE_LABELS[s] || s}
                    </button>
                  ))}
                </div>
                {/* Iter-97 · Frequency selector for the 4 expansion
                    services. Only rendered for the services the
                    registrant actually checked in the pill list above. */}
                {(() => {
                  const chosen = services.filter((s) => EXPANSION_FREQ_OPTIONS[s]);
                  if (chosen.length === 0) return null;
                  return (
                    <div className="mt-3 p-3 rounded-lg border border-zinc-700 bg-zinc-900/60" data-testid="register-service-frequencies-block">
                      <p className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold mb-2">
                        Service Frequencies
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {chosen.map((s) => (
                          <label key={s} className="flex min-w-0 flex-col items-stretch gap-2 text-[11px] text-zinc-200"
                                 data-testid={`register-service-frequency-row-${s}`}>
                            <span className="min-w-0 font-semibold">
                              {SERVICE_LABELS[s] || s}
                            </span>
                            <select
                              value={serviceFrequencies[s] || ''}
                              onChange={(e) => setServiceFrequencies((f) => ({ ...f, [s]: e.target.value }))}
                              className="w-full min-w-0 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-xs"
                              data-testid={`register-service-frequency-${s}`}
                            >
                              <option value="">— Recommend for me —</option>
                              {(EXPANSION_FREQ_OPTIONS[s] || []).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <Field label="Notes (optional)">
                <textarea rows={3} value={form.notes} onChange={(e) => update('notes', e.target.value)} maxLength={2000}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-white text-sm"
                          data-testid="register-notes" />
              </Field>

              <RegistrationAccessFields catalog={selectedType.amenities || []} amenities={amenities} setAmenities={setAmenities}
                form={form} setForm={setForm} />

              {/* Iter-108.2 · Property Manager Account block · Password
                  labels with star up top, live strength meter, match
                  indicator, single "reveal both" eye toggle, and paste
                  guard for suspicious clipboard content. */}
              <div
                className="p-4 rounded-lg bg-[#C9A962]/10 border border-[#C9A962]/40"
                data-testid="register-account-block"
              >
                <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
                  <p className="text-xs uppercase tracking-widest text-[#C9A962] font-bold">
                    Property Manager Account
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !showPassword;
                      setShowPassword(next);
                      setShowConfirmPassword(next);
                    }}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full transition"
                    style={{ backgroundColor: '#ffffff', color: '#C9A962', border: '1px solid #C9A962' }}
                    aria-label={showPassword && showConfirmPassword ? 'Hide both passwords' : 'Reveal both passwords'}
                    data-testid="register-reveal-both"
                  >
                    {showPassword && showConfirmPassword
                      ? <><EyeOff className="h-3 w-3" /> Hide both</>
                      : <><Eye className="h-3 w-3" /> Reveal both</>}
                  </button>
                </div>
                <p className="text-[11px] text-zinc-400 mb-3">
                  Set a password for your PM dashboard. Your login stays inactive
                  until our Customer Service team activates you.
                </p>

                {/* Iter-190 · Position selection — surfaces in the account's
                    top-logo menu (IdentityBadge) once activated. Options
                    swap between Valet (Multifamily) and Cleaners (12
                    commercial verticals) based on the property_type
                    picked above. */}
                <div className="mb-4" data-testid="register-position-block">
                  <span className="block text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-1">
                    <span className="text-[#C9A962] mr-1">*</span>Your position on this property
                  </span>
                  <select
                    required
                    value={form.pm_sub_role || ''}
                    onChange={(e) => update('pm_sub_role', e.target.value)}
                    className="w-full h-10 rounded-md bg-zinc-800 border border-zinc-700 text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD60A]"
                    data-testid="register-position-select"
                  >
                    <option value="">Choose your position…</option>
                    {(() => {
                      // Iter-190b · Per-vertical position catalog. Every
                      // list starts with Owner and stacks in real-world
                      // authority order so the dropdown maps to how each
                      // business actually staffs its property.
                      const POSITIONS_BY_TYPE = {
                        residential: [
                          ['owner', 'Owner'],
                          ['regional_property_manager', 'Regional Property Manager'],
                          ['property_manager', 'Property Manager'],
                          ['assistant_property_manager', 'Assistant Property Manager'],
                          ['leasing_manager', 'Leasing Manager'],
                          ['leasing_staff', 'Leasing Staff'],
                          ['concierge', 'Concierge'],
                          ['maintenance_supervisor', 'Maintenance Supervisor'],
                          ['maintenance_tech', 'Maintenance Technician'],
                        ],
                        office: [
                          ['owner', 'Owner'],
                          ['building_manager', 'Building Manager'],
                          ['assistant_building_manager', 'Assistant Building Manager'],
                          ['facilities_director', 'Facilities Director'],
                          ['facilities_manager', 'Facilities Manager'],
                          ['operations_coordinator', 'Operations Coordinator'],
                          ['office_manager', 'Office Manager'],
                          ['maintenance_lead', 'Maintenance Lead'],
                          ['front_desk', 'Front Desk / Reception'],
                        ],
                        retail: [
                          ['owner', 'Owner'],
                          ['general_manager', 'General Manager'],
                          ['assistant_general_manager', 'Assistant General Manager'],
                          ['store_manager', 'Store Manager'],
                          ['operations_manager', 'Operations Manager'],
                          ['facilities_manager', 'Facilities Manager'],
                          ['maintenance_lead', 'Maintenance Lead'],
                        ],
                        mall: [
                          ['owner', 'Owner'],
                          ['general_manager', 'General Manager'],
                          ['assistant_general_manager', 'Assistant General Manager'],
                          ['operations_director', 'Operations Director'],
                          ['tenant_coordinator', 'Tenant Coordinator'],
                          ['facilities_manager', 'Facilities Manager'],
                          ['housekeeping_supervisor', 'Housekeeping Supervisor'],
                          ['security_manager', 'Security Manager'],
                        ],
                        gas_station: [
                          ['owner', 'Owner'],
                          ['store_manager', 'Store Manager'],
                          ['assistant_manager', 'Assistant Manager'],
                          ['shift_supervisor', 'Shift Supervisor'],
                          ['maintenance_lead', 'Maintenance Lead'],
                        ],
                        restaurant: [
                          ['owner', 'Owner / Owner-Operator'],
                          ['general_manager', 'General Manager'],
                          ['assistant_general_manager', 'Assistant General Manager'],
                          ['kitchen_manager', 'Kitchen Manager'],
                          ['operations_manager', 'Operations Manager'],
                          ['maintenance_lead', 'Maintenance Lead'],
                        ],
                        school: [
                          ['owner', 'Owner / Board Chair'],
                          ['principal', 'Principal / Head of School'],
                          ['assistant_principal', 'Assistant Principal'],
                          ['facilities_director', 'Facilities Director'],
                          ['facilities_manager', 'Facilities Manager'],
                          ['operations_manager', 'Operations Manager'],
                          ['head_custodian', 'Head Custodian'],
                        ],
                        medical: [
                          ['owner', 'Owner / Practice Partner'],
                          ['administrator', 'Administrator'],
                          ['assistant_administrator', 'Assistant Administrator'],
                          ['facilities_director', 'Facilities Director'],
                          ['facilities_manager', 'Facilities Manager'],
                          ['operations_manager', 'Operations Manager'],
                          ['compliance_officer', 'Compliance Officer (OSHA/HIPAA)'],
                        ],
                        warehouse: [
                          ['owner', 'Owner'],
                          ['warehouse_manager', 'Warehouse Manager'],
                          ['assistant_manager', 'Assistant Manager'],
                          ['operations_manager', 'Operations Manager'],
                          ['facilities_manager', 'Facilities Manager'],
                          ['safety_officer', 'Safety Officer'],
                          ['maintenance_lead', 'Maintenance Lead'],
                        ],
                        church: [
                          ['owner', 'Owner / Board Trustee'],
                          ['senior_pastor', 'Senior Pastor'],
                          ['executive_pastor', 'Executive Pastor'],
                          ['administrator', 'Church Administrator'],
                          ['assistant_administrator', 'Assistant Administrator'],
                          ['facilities_manager', 'Facilities Manager'],
                          ['maintenance_lead', 'Maintenance Lead'],
                        ],
                        bank: [
                          ['owner', 'Owner / Board Member'],
                          ['branch_manager', 'Branch Manager'],
                          ['assistant_branch_manager', 'Assistant Branch Manager'],
                          ['operations_manager', 'Operations Manager'],
                          ['facilities_director', 'Facilities Director'],
                          ['facilities_manager', 'Facilities Manager'],
                          ['security_manager', 'Security Manager'],
                        ],
                        stadium: [
                          ['owner', 'Owner'],
                          ['general_manager', 'General Manager'],
                          ['assistant_general_manager', 'Assistant General Manager'],
                          ['operations_director', 'Operations Director'],
                          ['facilities_director', 'Facilities Director'],
                          ['event_operations_manager', 'Event Operations Manager'],
                          ['housekeeping_supervisor', 'Housekeeping Supervisor'],
                          ['security_manager', 'Security Manager'],
                        ],
                        airport: [
                          ['owner', 'Owner / Authority Board'],
                          ['airport_director', 'Airport Director'],
                          ['assistant_director', 'Assistant Director'],
                          ['operations_manager', 'Operations Manager'],
                          ['terminal_manager', 'Terminal Manager'],
                          ['facilities_director', 'Facilities Director'],
                          ['housekeeping_supervisor', 'Housekeeping Supervisor'],
                          ['security_manager', 'Security Manager'],
                        ],
                        other: [
                          ['owner', 'Owner'],
                          ['general_manager', 'General Manager'],
                          ['assistant_manager', 'Assistant Manager'],
                          ['operations_manager', 'Operations Manager'],
                          ['facilities_manager', 'Facilities Manager'],
                          ['maintenance_lead', 'Maintenance Lead'],
                        ],
                      };
                      const list = POSITIONS_BY_TYPE[selectedType?.key] || POSITIONS_BY_TYPE.other;
                      return list.map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ));
                    })()}
                  </select>
                  <p className="mt-1 text-[10px] text-zinc-500">
                    Your position, badge, and Property/Facility ID appear directly under your dashboard top logo once activated.
                  </p>
                </div>

                {pastedNotice && (
                  <p
                    className="text-[10px] font-bold px-2 py-1.5 rounded mb-2"
                    style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.4)' }}
                    data-testid="register-paste-guard"
                  >
                    ⚠️ You pasted content that looks like an email or a company name. Double-check — passwords should never contain your identity.
                  </p>
                )}
                <Row>
                  <div data-testid="register-password-field">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
                        <span className="text-[#C9A962] mr-1">*</span>Password
                        <span className="ml-1 normal-case tracking-normal text-zinc-500">(min 8 chars)</span>
                      </span>
                      {form.password && (
                        <span
                          className="text-[9px] uppercase tracking-widest font-bold"
                          style={{ color: passwordStrength.tone }}
                          data-testid="register-password-strength-label"
                        >
                          {passwordStrength.label}
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      <Input
                        required
                        type={showPassword ? 'text' : 'password'}
                        minLength={8}
                        value={form.password}
                        onChange={(e) => update('password', e.target.value)}
                        onPaste={(e) => guardPaste(e, 'password')}
                        className="bg-zinc-800 border-zinc-700 text-white pr-10"
                        data-testid="register-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-2 flex items-center text-zinc-400 hover:text-[#C9A962]"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        data-testid="register-password-eye"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {/* Live strength meter */}
                    <div
                      className="mt-1.5 grid grid-cols-4 gap-1"
                      data-testid="register-password-strength-meter"
                      data-score={passwordStrength.score}
                    >
                      {[1, 2, 3, 4].map((tick) => (
                        <span
                          key={tick}
                          className="h-1 rounded-full transition"
                          style={{ backgroundColor: passwordStrength.score >= tick ? passwordStrength.tone : '#3f3f46' }}
                        />
                      ))}
                    </div>
                  </div>
                  <div data-testid="register-confirm-password-field">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
                        <span className="text-[#C9A962] mr-1">*</span>Confirm Password
                      </span>
                      {form.confirm_password && (
                        passwordsMatch ? (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold"
                            style={{ color: '#22c55e' }}
                            data-testid="register-password-match-ok"
                          >
                            <CheckCircle2 className="h-3 w-3" /> Match
                          </span>
                        ) : (
                          <span
                            className="text-[9px] uppercase tracking-widest font-bold"
                            style={{ color: '#ef4444' }}
                            data-testid="register-password-match-nomatch"
                          >
                            No match
                          </span>
                        )
                      )}
                    </div>
                    <div className="relative">
                      <Input
                        required
                        type={showConfirmPassword ? 'text' : 'password'}
                        minLength={8}
                        value={form.confirm_password}
                        onChange={(e) => update('confirm_password', e.target.value)}
                        onPaste={(e) => guardPaste(e, 'confirm_password')}
                        className="bg-zinc-800 border-zinc-700 text-white pr-10"
                        data-testid="register-confirm-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute inset-y-0 right-2 flex items-center text-zinc-400 hover:text-[#C9A962]"
                        tabIndex={-1}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        data-testid="register-confirm-password-eye"
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </Row>
              </div>

              {/* Iter-95 · Consent checkboxes (both required). */}
              <div className="p-4 rounded-lg bg-zinc-950 border border-zinc-700 space-y-3" data-testid="register-consents-block">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.sms_opt_in}
                    onCheckedChange={(v) => update('sms_opt_in', !!v)}
                    data-testid="register-sms-optin"
                  />
                  <span className="text-xs text-zinc-200 leading-relaxed">
                    I agree to receive service SMS notifications from Noble Empire Cleaning Services via Twilio
                    (route updates, missed-pickup alerts, and account notices). Message frequency varies. Message
                    and data rates may apply. Reply <b>STOP</b> to unsubscribe.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.notifications_consent}
                    onCheckedChange={(v) => update('notifications_consent', !!v)}
                    data-testid="register-notif-consent"
                  />
                  <span className="text-xs text-zinc-200 leading-relaxed">
                    I authorize Noble Empire Cleaning Services to send in-app notifications, email confirmations,
                    and dashboard updates related to my property and services.
                  </span>
                </label>
              </div>

              {/* Iter-110 · Item 4 — Returning-registrant resume prompt.
                  Surfaced when the entered email already has a saved draft. */}
              {resumePrompt && (
                <div
                  className="p-3 rounded-lg flex items-center gap-3 flex-wrap"
                  style={{ backgroundColor: 'rgba(201,169,98,0.1)', border: '1px solid #C9A962' }}
                  data-testid="register-resume-prompt"
                >
                  <p className="text-xs text-white flex-1 min-w-[200px]">
                    <span className="font-bold" style={{ color: '#C9A962' }}>Welcome back!</span>{' '}
                    We found a saved draft for {resumePrompt.email}. Resume where you left off?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => { applyDraft(resumePrompt.data); setResumePrompt(null); }}
                      className="text-[11px] font-bold h-8"
                      style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
                      data-testid="register-resume-prompt-accept"
                    >
                      Resume Draft
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setResumePrompt(null)}
                      className="text-[11px] font-bold h-8"
                      style={{ backgroundColor: 'transparent', color: '#e5e5e5', border: '1px solid #6b6b6b' }}
                      data-testid="register-resume-prompt-dismiss"
                    >
                      Start Fresh
                    </Button>
                  </div>
                </div>
              )}

              {err && <p className="text-xs text-red-400" data-testid="register-error">{err}</p>}
              {draftMsg && (
                <p className="text-xs" style={{ color: draftMsg.ok ? '#34d399' : '#f87171' }} data-testid="register-draft-msg-form">
                  {draftMsg.text}
                </p>
              )}

              {/* Iter-109/110 · Save Draft & Finish Later + auto-save status. */}
              {!isDemo && (
                <>
                  <Button
                    type="button"
                    onClick={saveDraft}
                    disabled={draftBusy}
                    className="w-full font-bold text-sm py-5"
                    style={{ backgroundColor: 'transparent', color: '#C9A962', border: '1px dashed #C9A962' }}
                    data-testid="register-save-draft-btn"
                  >
                    {draftBusy ? 'Saving draft…' : '💾 Save Draft & Finish Later'}
                  </Button>
                  {autoSavedAt && (
                    <p className="text-[10px] text-center" style={{ color: '#34d399' }} data-testid="register-autosave-indicator">
                      ✓ Draft auto-saved at {autoSavedAt} — keyed to your email for 30 days
                    </p>
                  )}
                </>
              )}

              <Button type="submit" disabled={submitting} className="w-full font-bold text-base py-6"
                      style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
                      data-testid="register-submit">
                {submitting ? 'Submitting…' : 'Submit Registration'} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </form>
          </Card>
          </div>
        )}

        {step === 3 && result && (
          <Card
            className="mt-10 max-w-3xl mx-auto p-8 border-emerald-700 text-center backdrop-blur-md shadow-2xl"
            style={{ backgroundColor: 'rgba(6,78,59,0.55)' }}
            data-testid="register-step-confirm-card"
          >
            <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-400 mb-4" />
            <h2 className="text-2xl font-black text-white mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {result.is_demo ? 'DEMO SANDBOX READY' : 'REGISTRATION RECEIVED'}
            </h2>
            <p className="text-sm text-zinc-200 mb-4">{result.message}</p>
            <p className="text-[11px] text-zinc-500 mb-6">Reference: <span className="font-mono text-zinc-300">{result.reg_id}</span></p>

            {/* Iter-93 · PM Demo Auto-Login. Full-width, one-tap entry into
                the sandbox PM dashboard using the demo session token the
                server returned. Session is watermarked + auto-expires 24h. */}
            {result.is_demo && result.demo_session_token && (
              <div className="mb-4">
                <Button
                  type="button"
                  onClick={() => {
                    // Persist the session token the same way normal login
                    // does so the auth context picks it up on redirect.
                    try {
                      localStorage.setItem('session_token', result.demo_session_token);
                      document.cookie = `session_token=${result.demo_session_token}; path=/; max-age=86400`;
                    } catch { /* ignore */ }
                      window.location.href = '/property-manager-dashboard';
                  }}
                  className="w-full font-black text-lg py-6"
                  style={{
                    backgroundColor: '#FFD60A',
                    color: '#1a1a1a',
                    fontFamily: 'Barlow Condensed, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                  }}
                  data-testid="register-demo-enter-btn"
                >
                  🚀 Launch My Live Demo
                </Button>
                <p className="text-[10px] text-amber-300 mt-2">
                  Sandbox is watermarked and isolated. Session auto-expires in 24 hours — no real account created.
                </p>
              </div>
            )}

            <Button
              type="button"
              onClick={() => { setStep(1); setSelectedType(null); setResult(null); setServices([]); setAmenities({}); setTier(null); setForm({ property_name: '', country: 'USA', state: '', city: '', zip_code: '', street_address: '', primary_contact: '', email: '', phone: '', preferred_start_date: '', unit_count: '', square_footage: '', number_of_floors: '', notes: '', property_type_other: '', password: '', confirm_password: '', gate_code: '', sms_opt_in: false, notifications_consent: false }); }}
              variant="outline"
              className="border-emerald-600 text-emerald-400"
              data-testid="register-submit-another"
            >
              Register another property
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, children, testid, badge }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">{label}</p>
        {badge}
      </div>
      {children}
    </div>
  );
}

// Iter-88 · Subtle "Location detected" confirmation. Uses CSS opacity
// transition so the fade at 3s reads smoothly rather than snapping off.
function GeoBadge({ fading }) {
  return (
    <span
      className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
      style={{
        backgroundColor: 'rgba(16,185,129,0.15)',
        color: '#34d399',
        border: '1px solid rgba(16,185,129,0.4)',
        opacity: fading ? 0 : 1,
        transition: 'opacity 1000ms ease-out',
      }}
      data-testid="register-geo-badge"
    >
      📍 Location detected
    </span>
  );
}

/**
 * Iter-108 · Password strength scorer for the PM registration flow.
 * Returns a bucket {score 0-4, label, tone (hex)} used by the live
 * meter beside the Password input.
 */
function scorePassword(pwd) {
  if (!pwd) return { score: 0, label: '', tone: '#3f3f46' };
  let score = 0;
  if (pwd.length >= 8) score += 1;
  if (pwd.length >= 12) score += 1;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score += 1;
  if (/\d/.test(pwd)) score += 0.5;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 0.5;
  const bucket = Math.min(4, Math.max(1, Math.round(score)));
  const map = {
    1: { label: 'Weak',      tone: '#ef4444' },
    2: { label: 'Fair',      tone: '#f59e0b' },
    3: { label: 'Strong',    tone: '#22c55e' },
    4: { label: 'Excellent', tone: '#10b981' },
  };
  return { score: bucket, ...map[bucket] };
}

